/**
 * GDSCardManager.js
 * Orchestrates all logic for product cards in Step 3 of the GDS Builder
 * by coordinating specialized service classes.
 */
import { GDSCardRenderer } from './GDSCardRenderer.js';
import { GDSCardDataService } from './GDSCardDataService.js';
import { GDSCardUI } from './GDSCardUI.js';
import { GDSUrlManager } from './GDSUrlManager.js';
import { GDSCartAndShareManager } from './GDSCartAndShareManager.js';
import { PROPAGATING_FIELDS } from './GDSModules.js';

export class GDSCardManager {
    constructor(builder) {
        if (!builder) {
            throw new Error('GDSCardManager: builder is required');
        }

        this.builder = builder;
        this.state = builder.stateManager;
        this.api = builder.apiService;
        this.uiFeedback = builder.uiFeedback;
        this.modalManager = builder.modalManager;
        this.urlManager = builder.urlManager;

        try {
            this.renderer = new GDSCardRenderer(this, this.modalManager);
            this.dataService = new GDSCardDataService(this);
            this.ui = new GDSCardUI(this);
        } catch (error) {
            console.error('Failed to initialize card manager services:', error);
            throw error;
        }
    }

    get contentContainer() {
        return this.builder?.contentContainer;
    }

    /**
     * Renders the entire card selection step using a coordinated, multi-phase process.
     */
// In file: GDSCardManager.js

    /**
     * Renders the entire card selection step using a coordinated, multi-phase process.
     * This version includes logic to apply the default selection state after rendering.
     */
    async renderCardSelectionStep() {
        if (!this.contentContainer) {
            console.error("[CardManager] Cannot render: contentContainer is missing.");
            return;
        }

        // console.log('[CardManager] renderCardSelectionStep: Starting render process');

        this.uiFeedback.showLoading();

        try {
            const mainCardsData = this.state.context.main_cards || {};
            const additionalCardsData = this.state.context.additional_cards || {};

            // Validation for required services
            if (!this.dataService || !this.renderer) {
                console.error('[CardManager] Missing services:', { dataService: this.dataService, renderer: this.renderer });
                throw new Error('Required services not initialized');
            }

            // Phase 1: Generate HTML for main cards
            const mainCardHtmlStrings = await this.dataService.processMainCardGroups(mainCardsData);

            // Phase 2: Generate HTML for additional cards
            let additionalCardsHtml = '';
            if (Object.keys(additionalCardsData).length > 0) {
                const additionalCardHtmlStrings = await this.dataService.processAdditionalCardGroups(additionalCardsData, mainCardsData, true);
                if (additionalCardHtmlStrings.join('').trim()) {
                    additionalCardsHtml += this.renderer.buildAdditionalCardsHeaderHTML();
                    additionalCardsHtml += additionalCardHtmlStrings.join('');
                    additionalCardsHtml += `</div></div>`; // Closes additional cards containers
                }
            }

            // Phase 3: Build the final HTML string and inject it into the DOM
            let finalHtml = this.renderer.buildCardSelectionHeaderHTML(this.state.context.context);
            finalHtml += mainCardHtmlStrings.join('');
            finalHtml += `</div></div>`; // Closes main cards containers
            finalHtml += additionalCardsHtml;
            finalHtml += `<div class="md:h-16 md:hidden"></div></div>`; // Closes the main content wrapper
            this.contentContainer.innerHTML = finalHtml;

            // Phase 4: Post-render setup with error checking
            await this.ui.setupImageLoadingForAllCards();

            // --- START: APPLY DEFAULT SELECTIONS ---
            // After rendering the base HTML, explicitly set the default state for main cards.
            // This ensures the view is correct before any user-saved selections might be applied.
            // console.log('[CardManager] Applying default selections to main cards.');
            const mainCardsContainer = this.contentContainer.querySelector('#main-cards-container');
            if (mainCardsContainer) {
                // Find all primary cards (the first card of each group) within the main cards container.
                const primaryMainCards = mainCardsContainer.querySelectorAll('.product-card:not([data-secondary-index]), .product-card[data-card-index="0"]');

                primaryMainCards.forEach(card => {
                    const isAvailable = card.dataset.variantAvailable === 'true';
                    // Select the card by default ONLY if it's available.
                    if (isAvailable) {
                        this.ui.setCardSelectedState(card, true);
                    }
                });
            }
            // --- END: APPLY DEFAULT SELECTIONS ---

            this.ui.runFinalStylingPass();

            // Safely setup card listeners (Corrected to access handlers via the builder instance)
            // if (this.builder.eventHandler && this.builder.eventHandler.cardInteractionEventHandler) {
            //     this.builder.eventHandler.cardInteractionEventHandler.setupListeners();
            // } else {
            //     // console.warn("[CardManager] cardInteractionEventHandler not found on builder - skipping listener setup");
            // }

            // Setup other components with error checking (Corrected to access managers via the builder instance)
            if (this.builder.cartAndShareManager) {
                this.builder.cartAndShareManager.setupListeners();
            } else {
                console.warn('[CardManager] No cartAndShareManager found on builder');
            }

            if (this.builder.setupStickyMobileBar) {
                this.builder.setupStickyMobileBar();
            } else {
                console.warn('[CardManager] No setupStickyMobileBar method found on builder');
            }

            // Update the subtotal and field visibility AFTER default selections have been applied.
            this.ui.updateSubtotal();
            this.ui.setupFieldVisibility();

        } catch (error) {
            console.error('[CardManager] Failed to render card selection step:', error);
            console.error('[CardManager] Error Stack:', error.stack);
            this.uiFeedback.showError('Error loading product selection. Please refresh the page.');

            // Provide a more helpful error message in development
            if (this.contentContainer) {
                this.contentContainer.innerHTML = `
                    <div class="text-red-500 p-4">
                        <p class="font-bold">Error loading products</p>
                        <p class="text-sm mt-2">Please try refreshing the page. If the problem persists, contact support.</p>
                        ${window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ?
                            `<details class="mt-4 text-xs">
                                <summary>Debug Info</summary>
                                <pre class="mt-2 bg-gray-100 p-2 rounded">${error.stack}</pre>
                            </details>` : ''}
                    </div>`;
            }
        } finally {
            this.uiFeedback.hideLoading();
        }

        if (this.builder.cardTutorial && this.state.currentStep === 3) {
            // Small delay to ensure everything is rendered
            setTimeout(() => {
                this.builder.cardTutorial.checkAndShowTutorial();
            }, 500);
        }
    }

    /**
     * Handles updates to related cards when a propagating field changes. This is a critical
     * piece of logic that ensures compatibility across the user's selections.
     */
    async handleRelatedCardsUpdate(changedField, newValue, sourceCard) {
        // Use case-insensitive comparison to handle variations in field naming
        const isPropagatingField = PROPAGATING_FIELDS.some(pf =>
            pf.toLowerCase() === changedField.toLowerCase()
        );
        if (!isPropagatingField) return;

        this.uiFeedback.showLoading();
        try {
            const allCardDefs = {
                ...(this.state.context.main_cards || {}),
                ...(this.state.context.additional_cards || {})
            };
            const sourceCardTitle = sourceCard.dataset.cardTitle;

            const updatePromises = Object.entries(allCardDefs).map(async ([cardTitle, cardData]) => {
                if (cardTitle === sourceCardTitle) return;

                const usesField = cardData.dropdowns?.some(d => Object.keys(d)[0] === changedField);
                if (!usesField) return;

                const isCompatible = this.dataService.cardDefinitionSupportsValue(cardData, changedField, newValue);
                const mainCardOfGroup = this.contentContainer.querySelector(`.product-card[data-card-title="${cardTitle}"]:not([data-secondary-index])`);

                if (isCompatible) {
                    if (mainCardOfGroup) {
                        await this.dataService.updateCardWithNewFieldValue(mainCardOfGroup, changedField, newValue);
                    } else {
                        // *** THIS IS THE FIX ***
                        // The card should exist but is missing from the DOM, so recreate it.
                        await this.recreateAndAddCardGroup(cardTitle, cardData);
                    }
                } else {
                    // This part is correct: remove the group if it's incompatible.
                    if (mainCardOfGroup) {
                        this.ui.removeCardGroup(mainCardOfGroup);
                    }
                }
            });
            await Promise.all(updatePromises);
        } catch (error) {
            console.error("Error handling related card update:", error);
            this.uiFeedback.showError("Failed to update related items.");
        } finally {
            this.ui.setupFieldVisibility();
            this.ui.updateSubtotal();
            this.uiFeedback.hideLoading();
        }
    }

    /**
     * Recreates a card group that was removed due to incompatibility.
     * This is called when a propagating change makes the card group valid again.
     * @param {string} cardTitle - The title of the card group to recreate.
     * @param {object} cardData - The configuration data for the card group.
     */
    async recreateAndAddCardGroup(cardTitle, cardData) {
        const container = this.contentContainer.querySelector('#additional-cards-container');
        if (!container) {
            console.error("Could not find additional cards container to restore card group.");
            return;
        }

        // 1. Get the most up-to-date propagating values from the currently rendered main cards.
        const propagatingValues = this.dataService.getPropagatingValuesFromRenderedMainCards();

        // 2. Determine the new compatible configuration for the card group.
        const { skus, selections } = this.dataService.determineAdditionalCardCompatibility(cardData, propagatingValues, false);

        // If no compatible configuration is found, do nothing.
        if (!skus || skus.length === 0) {
            return;
        }

        // 3. Generate the complete HTML for the new card group.
        const cardGroupHtml = await this.dataService.createCardGroupHTML(cardTitle, cardData, skus, selections, false, true);

        if (!cardGroupHtml.trim()) {
            return; // Don't proceed if no HTML was generated.
        }

        // 4. Create and append the new card(s) to the DOM.
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = cardGroupHtml;

        // Extract modals and cards separately
        const modals = [];
        const cards = [];

        // Separate modals from cards
        Array.from(tempDiv.children).forEach(element => {
            if (element.classList.contains('product-modal')) {
                modals.push(element);
            } else if (element.classList.contains('product-card')) {
                cards.push(element);
            }
        });

        // Append modals to the document body first
        modals.forEach(modal => {
            document.body.appendChild(modal);
        });

        // Then append cards to the container
        for (const newCard of cards) {
            container.appendChild(newCard);

            // 5. Run post-render setup for each new card.
            await this.ui.setupImageLoadingForCard(newCard);

            const isAvailable = newCard.getAttribute('data-variant-available') === 'true';
            // Additional cards are not selected by default, so set state to false.
            this.ui.setCardSelectedState(newCard, false);
            this.ui.updateCardAvailabilityStyling(newCard, isAvailable);
        }

        // 6. Update the total price.
        this.ui.updateSubtotal();
    }

    /**
     * Creates a completely isolated secondary card. This is an orchestration method.
     * @param {string} sku - The SKU of the product to create a card for.
     * @param {HTMLElement} mainCard - The primary card this is related to.
     * @param {HTMLElement} container - The DOM container to append the new card to.
     * @param {number} cardIndex - The index for the new secondary card.
     * @param {string} cardTitle - The title of the card group.
     * @returns {Promise<HTMLElement|null>} The newly created card element or null on failure.
     */
    async createSecondaryCardWithIsolation(sku, mainCard, container, cardIndex, cardTitle) {
        try {
            // 1. Fetch Data
            const product = await this.api.fetchProductData(sku);
            if (!product) {
                throw new Error(`Product data not found for SKU: ${sku}`);
            }

            // 2. Render HTML
            const cardDataForRender = { header_question: cardTitle };
            const cardHtml = this.renderer.createProductCardHTML(
                product,
                cardTitle,
                cardDataForRender,
                false, // isMainCard
                true,  // isAdditionalCard
                cardIndex
            );

            // 3. Create Element & Append
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = cardHtml.trim();
            const newCard = tempDiv.firstElementChild;
            if (!newCard) { throw new Error("Failed to create card element from HTML."); }

            newCard.dataset.isSecondaryProduct = 'true';
            newCard.dataset.directSku = sku;
            newCard.dataset.noPropagate = 'true';

            const allGroupCards = container.querySelectorAll(`.product-card[data-card-title="${cardTitle}"]`);
            const lastCardInGroup = allGroupCards.length > 0 ? allGroupCards[allGroupCards.length - 1] : mainCard;
            lastCardInGroup.after(newCard);

            // 4. Post-Insertion UI and Event Setup
            await this.ui.setupImageLoadingForCard(newCard);

            // IMPORTANT: Only select if it's in the main cards container AND available
            const isInMainContainer = container.id === 'main-cards-container';
            const isAvailable = newCard.getAttribute('data-variant-available') === 'true';

            if (isInMainContainer && isAvailable) {
                this.ui.setCardSelectedState(newCard, true);
            } else {
                // Ensure it's not selected if unavailable or in additional cards
                this.ui.setCardSelectedState(newCard, false);
            }

            return newCard;

        } catch (error) {
            console.error(`[CardManager] Error creating isolated secondary card for SKU ${sku}:`, error);
            return null;
        }
    }
}
